import type { Instance, Project, TierConfig, TierConfigs } from '@nx/state';
import { NEO4J_MANAGED_KEY, PROJECT_PROFILE, TIER } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';

import {
  enterpriseDefaults,
  gbStringToInt,
  getDefaultName,
  getValidSize,
  mteDefaults,
  proDefaults,
} from '../entities/helpers';
import type { InstanceFormData } from '../entities/model';

export const isRealTierUpgrade = (oldInstance: Instance, newTier: TIER) => {
  if (newTier === TIER.MTE && oldInstance.capabilities.tier_upgrade.enabled) {
    return true;
  }
  return false;
};

export const defaults = (
  instance: Instance,
  targetTier: TIER,
  tierConfig: TierConfig,
  project: Project,
): InstanceFormData => {
  const defaultName = getDefaultName(instance.name, 'Upgrade');

  let data: InstanceFormData;

  switch (targetTier) {
    case TIER.FREE:
    case TIER.GDS:
    case TIER.PROFESSIONAL:
      data = {
        ...proDefaults(
          tierConfig,
          [],
          project,
          project.availableActions.create_pro_trial.enabled && project.profile === PROJECT_PROFILE.EXTERNAL,
        ),
        name: defaultName,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
      };
      break;
    case TIER.MTE:
      data = {
        ...mteDefaults(tierConfig, [], project),
        name: instance.name,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
      };
      break;
    case TIER.AURA_DSE:
    case TIER.ENTERPRISE:
      data = {
        ...enterpriseDefaults(tierConfig, project),
        name: defaultName,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
        encryptionKeyRef: instance.encryptionKeyRef ?? NEO4J_MANAGED_KEY,
      };
      break;
    default:
      throw new Error('Tier not supported');
  }

  const size = getValidSize(data, project, tierConfig.sizes, {
    resizeThreshold: isNotNullish(instance.desiredSettings.storage)
      ? gbStringToInt(instance.desiredSettings.storage)
      : undefined,
    isTrial:
      project.availableActions.create_pro_trial.enabled &&
      project.profile === PROJECT_PROFILE.EXTERNAL &&
      instance.tier === TIER.FREE &&
      targetTier === TIER.PROFESSIONAL,
    defaultSize: instance.desiredSettings,
  });
  return { ...data, size };
};

export const tierConfigsForTrueUpgrade = ({
  tierConfigs,
  instance,
}: {
  tierConfigs: TierConfigs;
  instance: Instance;
}): TierConfig | null => {
  const { cloudProvider, desiredSettings } = instance;
  const tierConfig = tierConfigs[TIER.MTE];
  if (isNullish(tierConfig)) {
    return null;
  }
  const cloudProviderRegions = (tierConfig.cloudProviderRegions[cloudProvider] ?? []).filter(
    (region) => region.name === instance.region,
  );

  const storage = gbStringToInt(
    isNotNullish(desiredSettings.storage) ? desiredSettings.storage : (tierConfig.sizes[0]?.storage ?? ''),
  );
  const availableSizes = tierConfig.sizes.filter((size) => gbStringToInt(size.storage) >= storage);
  const versions = tierConfig.versions.filter(
    (version) => parseInt(version, 10) >= parseInt(desiredSettings.version, 10),
  );

  return {
    sizes: availableSizes,
    defaultVersion: tierConfig.defaultVersion,
    // Allow users to clone from Free to any other CSP.
    // Also allow if the Cross CSP Cloning flag is enabled
    cloudProviderRegions: {
      [instance.cloudProvider]: cloudProviderRegions,
    },
    tier: instance.tier,
    tierDisplayName: instance.tierDisplayName,
    versions,
    secondaryCost: tierConfig.secondaryCost,
    priorities: tierConfig.priorities,
  };
};
